import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import {
  listCatalogueTenants,
  listCatalogueForTenant,
  setCatalogueEnabled,
  type CatalogueTenant,
  type CatalogueEntry,
} from '../../api/brandingCatalogueApi.js';

// Super admin ticks which branding types and items a tenant may use.
// Un-ticking deactivates the tenant's row; it never deletes it, so work orders
// and claims that already reference the item keep resolving.

export function BrandingCatalogueAssignmentPage(): ReactNode {
  const { token } = useAuth();
  const [tenants, setTenants] = useState<CatalogueTenant[] | null>(null);
  const [tenantId, setTenantId] = useState('');
  const [entries, setEntries] = useState<CatalogueEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const rows = await listCatalogueTenants(token);
        setTenants(rows);
        if (rows.length && !tenantId) setTenantId(rows[0].tenantId);
      } catch (e) {
        setError((e as Error).message);
      }
    })();
    // Tenant list is fetched once; selecting a tenant must not refetch it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadEntries = useCallback(async () => {
    if (!tenantId) return;
    setEntries(null);
    try {
      setEntries(await listCatalogueForTenant(token, tenantId));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [token, tenantId]);

  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  const toggle = useCallback(
    async (entry: CatalogueEntry, next: boolean) => {
      setBusy(entry.catalogueCode);
      try {
        await setCatalogueEnabled(token, tenantId, entry.catalogueCode, next);
        await loadEntries();
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(null);
      }
    },
    [token, tenantId, loadEntries],
  );

  // Items are grouped under the type they belong to, so a super admin sees the
  // same shape the tenant will see in its own screens.
  const grouped = useMemo(() => {
    if (!entries) return [];
    const types = entries.filter((e) => e.isType);
    return types.map((t) => ({
      type: t,
      items: entries.filter((e) => e.parentCode === t.catalogueCode),
    }));
  }, [entries]);

  return (
    <main aria-label="branding-catalogue-assignment">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>Branding Catalogue Assignment</h1>
      <p style={{ marginTop: 0, marginBottom: 16, color: 'var(--color-text-muted)' }}>
        Choose which branding types and items each tenant may use. Un-ticking hides
        the entry from the tenant without deleting anything already recorded against it.
      </p>

      <div style={{ marginBottom: 16 }}>
        <label htmlFor="tenant-picker" style={{ fontSize: 12, display: 'block' }}>
          Tenant
        </label>
        <select
          id="tenant-picker"
          data-testid="tenant-picker"
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          style={{ padding: '4px 8px', minWidth: 280 }}
        >
          {(tenants ?? []).map((t) => (
            <option key={t.tenantId} value={t.tenantId}>
              {t.tenantCode} — {t.tenantName}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div role="alert" data-testid="catalogue-error" style={{ color: 'var(--color-danger, #d32f2f)', marginBottom: 12 }}>
          {error}
        </div>
      )}

      {entries === null ? (
        <p data-testid="catalogue-loading">Loading…</p>
      ) : grouped.length === 0 ? (
        <p data-testid="catalogue-empty">The platform catalogue is empty.</p>
      ) : (
        grouped.map(({ type, items }) => (
          <section key={type.catalogueCode} data-testid={`catalogue-type-${type.catalogueCode}`} style={{ marginBottom: 20 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600 }}>
              <input
                type="checkbox"
                data-testid={`toggle-${type.catalogueCode}`}
                checked={type.enabled}
                disabled={busy === type.catalogueCode}
                onChange={(e) => void toggle(type, e.target.checked)}
              />
              {type.catalogueName}
              {!type.materialised && (
                <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--color-text-muted)' }}>
                  not provisioned
                </span>
              )}
            </label>

            <ul style={{ listStyle: 'none', paddingLeft: 28, marginTop: 8 }}>
              {items.map((it) => (
                <li key={it.catalogueCode} style={{ marginBottom: 4 }}>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      data-testid={`toggle-${it.catalogueCode}`}
                      checked={it.enabled}
                      disabled={busy === it.catalogueCode || !type.enabled}
                      onChange={(e) => void toggle(it, e.target.checked)}
                    />
                    <span>{it.catalogueName}</span>
                    {!it.materialised && (
                      <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                        not provisioned
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </main>
  );
}
